import { useToken } from '@ori/auth';
import { TranslationsNamespaceProvider, TranslationsProvider } from '@ori/i18n';
import { ApiClientProvider } from '@ori/static-api-client-react';
import type { FunctionComponent } from 'react';
import { Suspense, useCallback, useEffect, useState } from 'react';

import { APP_NAME, defaultTranslations } from '../constants';
import { useProductInfoDialog } from '../hooks';
import { useCloseEvents, useOpenEvent } from '../hooks/internal';
import type { ProductInfoDialogControllerProps } from './types';

const TranslatedController = ({
  currentLanguage,
  globalApiUrl,
  graphqlUrl,
  staticApiUrl,
  tenant,
}: Omit<ProductInfoDialogControllerProps, 'translations'>) => {
  const [open, setOpen] = useState(false);
  const onClose = useCallback(() => setOpen(false), []);
  const onOpen = useCallback(() => setOpen(true), []);
  const { customerId, isAnonymousUser, token } = useToken();
  const {
    atpClusterId,
    initialProductCode,
    lineNumber,
    mode,
    originalItemCode,
    originalQuantity,
    placement,
    position,
    productCodes,
    replaceSubscription,
  } = useOpenEvent({ onOpen });

  const { closeProductInfoDialog, openProductInfoDialog, productInfoDialog } = useProductInfoDialog({
    atpClusterId,
    analytics: {
      lineNumber,
      position,
      placement,
    },
    currentLanguage,
    customer: {
      customerId,
      isAnonymousUser,
    },
    dialog: { onClose },
    globalApiUrl,
    graphqlUrl,
    initialProductCode,
    mode,
    productCodes,
    shared: {
      originalItemCode,
      originalQuantity,
    },
    staticApiUrl,
    tenant,
    token,
    replaceSubscription,
  });
  useCloseEvents({ onClose });
  useEffect(() => {
    if (open) {
      openProductInfoDialog();
    } else {
      closeProductInfoDialog();
    }
  }, [closeProductInfoDialog, open, openProductInfoDialog]);

  return productInfoDialog;
};

export const ProductInfoDialogController: FunctionComponent<ProductInfoDialogControllerProps> = (props) => {
  const { currentLanguage, staticApiUrl, tenant, translations } = props;
  const { token } = useToken();

  return (
    <Suspense fallback={null}>
      <TranslationsProvider
        // eslint-disable-next-line no-console -- we want to log errors
        clientFetchConfiguration={{ baseUrl: staticApiUrl, onError: console.error, tenant, token }}
        currentLanguage={currentLanguage}
        defaultTranslationsWithNamespaces={{ [APP_NAME]: defaultTranslations }}
        translationsWithNamespaces={translations ? { [APP_NAME]: translations } : undefined}
      >
        <TranslationsNamespaceProvider namespace={APP_NAME}>
          <ApiClientProvider
            tenant={tenant}
            baseUrl={staticApiUrl}
            token={token}
          >
            <TranslatedController {...props} />
          </ApiClientProvider>
        </TranslationsNamespaceProvider>
      </TranslationsProvider>
    </Suspense>
  );
};
